import React from 'react';
import { formatDate } from '@koob/dates';
import {
  ExtraRequirementEnum,
  ServiceApplicationDateKindEnum,
} from '@koob/enums';
import {
  formatCurrency,
  getExtraAmountPerPax,
  getExtraOccurrencePax,
  getServiceApplicationDate,
  sortExtraOccurrences,
} from '@koob/utils';
import AdditionalServiceChip from '../AdditionalServiceChip';
import { getExtraAmount } from '../../logic/serviceExtras';
import type { Translate } from './types';

interface TripDayExperienceServiceChipsProps {
  extras: any[];
  scope: any;
  departureDate: unknown;
  serviceFrame: { startDayIndex: number; endDayIndex: number };
  // KD-3805: the experience's catalogue, to tell a global transfer apart.
  entries?: any[];
  currency: string;
  onRemove?: (extra: any) => void;
  t: Translate;
}

function getDateLabel(application: any, t: Translate) {
  if (!application) {
    return null;
  }

  return application.kind === ServiceApplicationDateKindEnum.RANGE
    ? t('compose.services.dateRange', {
        from: formatDate(application.from, 'DD/MM/YYYY'),
        to: formatDate(application.to, 'DD/MM/YYYY'),
      })
    : formatDate(application.from, 'DD/MM/YYYY');
}

// KD-3650 — read like `getExtraAmount`, so the travellers the chip names are
// the ones its amount was computed on.
function getTravellersLabel(extra: any, t: Translate) {
  const { adults, childrenBirthdates } = getExtraOccurrencePax(extra);
  const travellers = [
    adults ? t('compose.services.adults', { count: adults }) : null,
    childrenBirthdates.length
      ? t('compose.services.children', { count: childrenBirthdates.length })
      : null,
  ].filter(Boolean);

  return travellers.length ? travellers.join(' · ') : null;
}

// KD-3744 — the per-traveller share stated under an amount. A line retained
// for nobody has none, and the label is then left out.
function getPerPaxLabel(extra: any, currency: string, t: Translate) {
  const amount = getExtraAmountPerPax(extra);

  return amount == null
    ? null
    : t('compose.services.perPax', {
        price: formatCurrency({
          amount,
          currency: extra?.currency ?? currency,
        }),
      });
}

export default function TripDayExperienceServiceChips({
  extras,
  scope,
  departureDate,
  serviceFrame,
  entries,
  currency,
  onRemove,
  t,
}: TripDayExperienceServiceChipsProps) {
  return (
    <>
      {sortExtraOccurrences(extras).map((extra: any) => (
        <AdditionalServiceChip
          key={`${extra.id}-${extra.dayIndex}-${extra.parentExtraId ?? 'root'}-${extra.occurrenceKey ?? 'single'}`}
          scope={scope}
          name={extra?.name ?? ''}
          isRequired={extra?.type === ExtraRequirementEnum.REQUIRED}
          requiredLabel={t('compose.services.required')}
          dateLabel={getDateLabel(
            getServiceApplicationDate({
              departureDate,
              extra,
              frame: serviceFrame,
              entries,
            }),
            t,
          )}
          travellersLabel={getTravellersLabel(extra, t)}
          priceLabel={
            extra?.price != null
              ? t('compose.services.amount', {
                  price: formatCurrency({
                    amount: getExtraAmount(extra),
                    currency: extra?.currency ?? currency,
                  }),
                })
              : null
          }
          perPaxLabel={
            extra?.price != null ? getPerPaxLabel(extra, currency, t) : null
          }
          onRemove={onRemove ? () => onRemove(extra) : undefined}
          removeLabel={t('compose.services.remove')}
        />
      ))}
    </>
  );
}
